/**
 * Picks View Constants
 * Static data and configurations for the picks feature
 */

// =============================================================================
// Types
// =============================================================================

import type {
  LegScoringStrategy,
  MlConfig,
  ParlayIntelligenceConfig,
} from '@parlay-picker/shared/analysisConfig';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import type { MarketOption } from '@parlay-picker/shared/marketKey';
import { AVAILABLE_MARKETS } from '@parlay-picker/shared/marketKey';
import {
  DEFAULT_MAX_LEG_COUNT,
  DEFAULT_MIN_LEG_COUNT,
  DEFAULT_TARGET_ODDS_MAX,
  DEFAULT_TARGET_ODDS_MIN,
} from '@parlay-picker/shared/strategyConstants';
export type { AnalysisMode };
export type { LegScoringStrategy, MlConfig, ParlayIntelligenceConfig };
export type { MarketOption };
export { AVAILABLE_MARKETS };

export interface AnalysisConfig {
  outputOptions: {
    analysisMode: AnalysisMode;
  };
  banList: {
    includeBannedPlayers: boolean;
  };
  propTypes: Record<string, boolean>;
  playerStats: {
    l5Average: boolean;
    l10Average: boolean;
    l20Average: boolean;
    seasonAverage: boolean;
  };
  advancedStats: {
    usageRate: boolean;
    trueShootingPct: boolean;
    assistRatio: boolean;
    offensiveRating: boolean;
    defensiveRating: boolean;
  };
  matchupData: {
    playerVsTeamHistory: boolean;
    playerPosition: boolean;
  };
  opponentDefense: {
    defVsPosition: boolean;
    reboundingAllowed: boolean;
    threePointDefense: boolean;
    pointsInPaint: boolean;
    fastBreakAllowed: boolean;
  };
  gameScript: {
    blowoutRisk: boolean;
    backToBack: boolean;
    paceImpact: boolean;
    gameTotal: boolean;
    homeAwaySplits: boolean; // Factor 27: Location-dependent performance
    revengeGame: boolean; // Factor 28: Recent blowout loss motivation
    recentForm: boolean; // Team L5 record and win streaks (tier confidence modifier)
  };
  otherFactors: {
    injuryStatus: boolean;
    historySource: 'personal' | 'community' | 'off';
    trendAnalysis: boolean;
    usageTrend: boolean; // Factor 16: Usage rate changes L5 vs L10
    minutesTrend: boolean; // Factor 17: Minutes changes L5 vs L10
    efficiencyTrend: boolean; // Factor 18: FG%/3PT% changes L5 vs L10
    includeSharpContext?: boolean; // Sharp money signals in AI prompt (Elite and up)
    includeTeamPropSignals?: boolean; // Opponent hit rate data in AI prompt (admin only)
    includeProjectedStarters?: boolean; // Projected lineups; needs MSF on the server
  };
  efficiencyMetrics: {
    bettingValueEfficiency: boolean; // Margin analysis (how much players beat/miss lines)
    lineValueDetection: boolean; // Alert when line is set below player's average
    historicalTrends: boolean; // Track improving/declining efficiency trends
  };
  parlayStrategy: {
    minLegCount: number; // Minimum legs per parlay (default 2)
    maxLegCount: number; // Maximum legs per parlay (default 5)
    targetOddsMin: number; // Min target odds (e.g., 150 for +150)
    targetOddsMax: number; // Max target odds (e.g., 350 for +350)
    directionPreference: 'balanced' | 'over' | 'under'; // Focus generation on a direction
  };
  ml?: MlConfig;
  parlayIntelligence?: ParlayIntelligenceConfig;
  // Index signature for dynamic section access
  [key: string]:
    | Record<string, boolean | string | number | undefined>
    | { analysisMode: AnalysisMode }
    | { includeBannedPlayers: boolean }
    | MlConfig
    | ParlayIntelligenceConfig
    | undefined
    | Record<string, boolean>;
}

export interface RiskTier {
  key: string;
  name: string;
  color: string;
  bgColor: string;
  label: string;
  desc: string;
}

export type { PicksFilter } from '@parlay-picker/shared/parlayFilters';

// =============================================================================
// Constants
// =============================================================================

// Default selected prop types (moneyline, points, rebounds, assists)
export const DEFAULT_SELECTED_MARKETS: string[] = [
  'h2h',
  'player_points',
  'player_rebounds',
  'player_assists',
];

// Factor on/off values must equal the registry's defaults (factorDefaults.contract.test.ts). Admin-only
// sections (ml, parlayIntelligence) are left out: a missing section means its defaults on the server.
export const DEFAULT_ANALYSIS_CONFIG: AnalysisConfig = {
  outputOptions: {
    analysisMode: 'standard' as AnalysisMode,
  },
  banList: {
    includeBannedPlayers: true,
  },
  propTypes: {
    // Game markets
    h2h: true,
    spreads: false,
    totals: false,
    alternate_spreads: false,
    alternate_totals: false,
    // Team markets
    team_totals: false,
    alternate_team_totals: false,
    // Basic player props
    player_points: true,
    player_rebounds: true,
    player_assists: true,
    player_threes: false,
    player_blocks: false,
    player_steals: false,
    player_turnovers: false,
    // Shooting props
    player_field_goals: false,
    player_frees_made: false,
    // Combo props
    player_blocks_steals: false,
    player_points_rebounds_assists: false,
    player_points_rebounds: false,
    player_points_assists: false,
    player_rebounds_assists: false,
    // Special props
    player_double_double: false,
    player_triple_double: false,
    player_first_basket: false,
    player_first_team_basket: false,
    // Alternate player props (X+ format — exact line = win)
    player_points_alternate: false,
    player_rebounds_alternate: false,
    player_assists_alternate: false,
    player_threes_alternate: false,
    player_steals_alternate: false,
    player_blocks_alternate: false,
    player_turnovers_alternate: false,
    player_field_goals_alternate: false,
    player_blocks_steals_alternate: false,
    player_points_rebounds_assists_alternate: false,
    player_points_rebounds_alternate: false,
    player_points_assists_alternate: false,
    player_rebounds_assists_alternate: false,
    // Half markets
    h2h_h1: false,
    h2h_h2: false,
    spreads_h1: false,
    spreads_h2: false,
    totals_h1: false,
    totals_h2: false,
    team_totals_h1: false,
    team_totals_h2: false,
    alternate_spreads_h1: false,
    alternate_spreads_h2: false,
    alternate_totals_h1: false,
    alternate_totals_h2: false,
    alternate_team_totals_h1: false,
    alternate_team_totals_h2: false,
    // Quarter markets
    h2h_q1: false,
    h2h_q2: false,
    h2h_q3: false,
    h2h_q4: false,
    spreads_q1: false,
    spreads_q2: false,
    spreads_q3: false,
    spreads_q4: false,
    totals_q1: false,
    totals_q2: false,
    totals_q3: false,
    totals_q4: false,
    team_totals_q1: false,
    team_totals_q2: false,
    team_totals_q3: false,
    team_totals_q4: false,
    // Q1 player props
    player_points_q1: false,
    player_rebounds_q1: false,
    player_assists_q1: false,
  },
  playerStats: {
    l5Average: true,
    l10Average: true,
    l20Average: false, // Disabled by default - redundant with L10 (highly correlated)
    seasonAverage: true,
  },
  advancedStats: {
    usageRate: false,
    trueShootingPct: false,
    assistRatio: false,
    offensiveRating: false,
    defensiveRating: false,
  },
  matchupData: {
    playerVsTeamHistory: false,
    playerPosition: false, // Disabled by default - defVsPosition provides more specific opponent defense data
  },
  opponentDefense: {
    defVsPosition: true, // Opponent's FG% allowed on threes, in the paint and overall
    reboundingAllowed: false, // Opponent's rebounding weakness
    threePointDefense: false, // Opponent 3PT% allowed
    pointsInPaint: false, // Opponent PIP allowed
    fastBreakAllowed: false, // Opponent fast break points allowed
  },
  gameScript: {
    blowoutRisk: true,
    backToBack: true,
    paceImpact: false,
    gameTotal: false, // Disabled by default - advanced factor, enable manually
    homeAwaySplits: true, // Factor 27: Enabled by default
    revengeGame: false, // Factor 28: Disabled by default (situational)
    recentForm: false, // Disabled by default — team momentum (Pro+ discovery)
  },
  otherFactors: {
    injuryStatus: true,
    historySource: 'off',
    trendAnalysis: false, // Disabled when historySource is 'off'
    usageTrend: false, // Disabled when historySource is 'off'
    minutesTrend: false, // Factor 17: Disabled by default (granular, trendAnalysis covers broad trends)
    efficiencyTrend: false, // Factor 18: Disabled by default (granular, trendAnalysis covers broad trends)
  },
  efficiencyMetrics: {
    bettingValueEfficiency: false, // Disabled by default - requires History Source to be enabled
    lineValueDetection: false, // Disabled by default - bettingValueEfficiency covers core value analysis
    historicalTrends: false, // Disabled by default - needs more data accumulation
  },
  parlayStrategy: {
    minLegCount: DEFAULT_MIN_LEG_COUNT,
    maxLegCount: DEFAULT_MAX_LEG_COUNT,
    targetOddsMin: DEFAULT_TARGET_ODDS_MIN,
    targetOddsMax: DEFAULT_TARGET_ODDS_MAX,
    directionPreference: 'balanced',
  },
};

export const RISK_TIERS: RiskTier[] = [
  {
    key: 'safe',
    name: 'Safe Play',
    color: 'border-theme-success',
    bgColor: 'bg-theme-success/15',
    label: 'SAFE PLAYS',
    desc: 'Low risk, consistent edges',
  },
  {
    key: 'value',
    name: 'Value Play',
    color: 'border-theme-primary',
    bgColor: 'bg-theme-primary/15',
    label: 'VALUE PLAYS',
    desc: 'Balanced risk/reward',
  },
  {
    key: 'upside',
    name: 'Upside Play',
    color: 'border-theme-warning',
    bgColor: 'bg-theme-warning/15',
    label: 'UPSIDE PLAYS',
    desc: 'Higher variance, bigger payouts',
  },
  {
    key: 'long',
    name: 'Long Shot',
    color: 'border-theme-danger',
    bgColor: 'bg-theme-danger/15',
    label: 'LONG SHOTS',
    desc: 'High risk, high reward',
  },
  {
    key: 'lottery',
    name: 'Lottery Ticket',
    color: 'border-purple-500',
    bgColor: 'bg-purple-500/15',
    label: 'LOTTERY TICKETS',
    desc: 'Very high risk, massive payouts',
  },
];

export { DEFAULT_PICKS_FILTER as DEFAULT_FILTERS } from '@parlay-picker/shared/parlayFilters';

// NBA player ID fallback map for headshots (NBA CDN uses official player IDs).
// The app dynamically fetches player IDs from the NBA Stats API on startup
// (see nbaPlayerIdService.ts). This map ONLY contains entries that the API
// doesn't cover: name variations, aliases, and inactive/injured players.
// DO NOT add players here that the API already returns — they'll be fetched automatically.
export const NBA_PLAYER_IDS: Record<string, number> = {
  // --- Name variations (odds API / AI may use different spelling than NBA.com) ---
  'Nicolas Claxton': 1629651, // API returns "Nic Claxton"
  'Nikola Vucevic': 202696, // API may use accented "Vučević"
  'R.J. Barrett': 1629628, // API returns "RJ Barrett"
  'A.J. Green': 1631260, // API returns "AJ Green"
  'C.J. McCollum': 203468, // API returns "CJ McCollum"
  'André Drummond': 203083, // Accented version (API returns "Andre Drummond")
  'Brandon Podziemski': 1641764, // Misspelling of "Brandin Podziemski"
  'Vincent Williams Jr': 1631246, // API returns "Vince Williams Jr."
  'Herb Jones': 1630529, // API returns "Herbert Jones"
  Fultz: 1628365, // Short name alias for Markelle Fultz

  // --- Players not on current-season API roster (injured, inactive, overseas) ---
  'Luka Doncic': 1629029,
  'Nikola Jokic': 203999,
  'Jimmy Butler': 202710,
  'Kristaps Porzingis': 204001,
  'Dennis Schroder': 203471,
  'Jusuf Nurkic': 203994,
  'Egor Demin': 1642856,
  'Carlton Carrington': 1642267,
  'Moussa Diabate': 1631217,
  'Gordon Hayward': 202330,
  'Nikola Jovic': 1631107,
  'Vit Krejci': 1630249,
  'Ron Holland': 1641842,
  'Markelle Fultz': 1628365,
  'Isaiah Stewart II': 1630191,
  'Bol Bol': 1629626,
  'Robert Williams': 1629057,
  'Jeenathan Williams': 1631466, // NBA.com lists him as "Nate Williams"
};
